import React from 'react';

import {RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {StepMeta} from '../types';
import {MediaBrowserFullscreenModal} from './MediaBrowserFullscreenModal';
import {
  MediaBrowserModalProvider,
  useMediaBrowserModalContext,
} from './MediaBrowserModalContext';
import {MediaBrowserModalProps} from './types';

// Re-export for convenience
export type {ModalData} from './types';

type MediaBrowserModalContainerProps = {
  children: React.ReactNode;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  sliderConfig: {
    snapToExistingStep: boolean;
    stepStrideLength: number;
  };
  stepMeta?: StepMeta;
  onModalClose?: (finalStep: number, finalIndex: number) => void;
  /*
   * NOTE: this prop allows unit tests to mock the underlying modal component.
   * It should only be used for testing purposes, and not provided directly outside of tests.
   */
  modalComponent?: React.ComponentType<MediaBrowserModalProps>;
};

// Separate component needed because useMediaBrowserModalContext() must be called
// inside the MediaBrowserModalProvider, but MediaBrowserModalContainer creates the provider.
// This prevents the "must be used within a MediaBrowserModalProvider" error.
const ModalContent: React.FC<{
  modalComponent?: React.ComponentType<MediaBrowserModalProps>;
}> = ({modalComponent}) => {
  const ModalComponent = modalComponent ?? MediaBrowserFullscreenModal;
  const {isOpen, modalData, closeModal, updateModalData} =
    useMediaBrowserModalContext();

  if (!isOpen || !ModalComponent) {
    return null;
  }

  return (
    <ModalComponent
      isOpen={isOpen}
      modalData={modalData}
      onClose={closeModal}
      updateModalData={updateModalData}
    />
  );
};

export const MediaBrowserModalContainer: React.FC<
  MediaBrowserModalContainerProps
> = ({
  children,
  modalComponent,
  runsWithMedia,
  sliderConfig,
  stepMeta,
  onModalClose,
}) => {
  return (
    <MediaBrowserModalProvider
      runsWithMedia={runsWithMedia}
      sliderConfig={sliderConfig}
      stepMeta={stepMeta}
      onModalClose={onModalClose}>
      {children}
      <ModalContent modalComponent={modalComponent} />
    </MediaBrowserModalProvider>
  );
};
